
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { useBluejayGroups } from "@/hooks/useBluejayGroups";
import { useToast } from "@/hooks/use-toast";

export const CreateBluejayGroupDialog = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [jamfId, setJamfId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  const { addGroup } = useBluejayGroups();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast({
        title: "Error",
        description: "Group name is required",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      console.log('Creating Bluejay group:', { name, description, jamfId });
      
      await addGroup({
        name: name.trim(),
        description: description.trim() || null,
        // Note: We'll need to add jamf_id field to the database schema
        // For now, we'll store it in the description with a prefix if provided
        ...(jamfId.trim() && {
          description: `[JAMF_ID:${jamfId.trim()}] ${description.trim()}`.trim()
        })
      });

      // Reset form
      setName("");
      setDescription("");
      setJamfId("");
      setOpen(false);
      
      toast({
        title: "Success",
        description: `Group "${name}" created successfully${jamfId ? ' with Jamf Pro integration' : ''}`,
      });
    } catch (error) {
      console.error("Error creating group:", error);
      toast({
        title: "Error",
        description: "Failed to create group. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setName("");
    setDescription("");
    setJamfId("");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#0f172a] hover:bg-[#1e293b] text-white">
          <Plus className="w-4 h-4 mr-2" />
          New Group
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Bluejay Group</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="group-name">Group Name *</Label>
            <Input
              id="group-name"
              placeholder="Enter group name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="group-description">Description</Label>
            <Textarea
              id="group-description"
              placeholder="Enter group description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="jamf-id">Jamf Pro Smart Group ID</Label>
            <Input
              id="jamf-id"
              placeholder="Enter Jamf Pro smart device group ID (optional)"
              value={jamfId}
              onChange={(e) => setJamfId(e.target.value)}
            />
            <p className="text-sm text-gray-500">
              If provided, the group scope will be automatically pulled from the Jamf Pro smart device group. 
              Leave empty to manually set the scope based on Bluejay devices.
            </p>
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
              disabled={isLoading}
            >
              {isLoading ? "Creating..." : "Create Group"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
